import cs from 'classnames';
import Head from 'next/head';
import { useRouter } from 'next/router';
import type { FC } from 'react';
import { Box, Container } from '@/DesignSystem/Component';
import useIsLandscape from '@/DesignSystem/Hook/useIsLandscape';
import { APPLICATION_LINK, APPLICATION_TITLE, FILE_BROWSER_URL } from '@/Module/Core/Constant';
import { replaceHalfSpace } from '@/Module/Core/Helper';
import imgProxyLoader from '@/Module/Core/Helper/imgProxyLoader';
import { LessonDetailRelatedVideos } from '@/Module/Lesson/Component';
import { selectedLessonSectionSelector } from '@/Module/Lesson/Store/Selector/selectedLessonSectionSelector';
import { useAppSelector } from '@/Module/Store/Hook';
import LessonDetailBookmark from './LessonDetailBookmark';
import LessonDetailBreadcrumb from './LessonDetailBreadcrumb';
import LessonDetailCommentList from './LessonDetailCommentList';
import LessonDetailCrashReport from './LessonDetailCrashReport';
import LessonDetailCrashReportButton from './LessonDetailCrashReportButton';
import LessonDetailDescription from './LessonDetailDescription';
import LessonDetailHeader from './LessonDetailHeader';
import LessonDetailLike from './LessonDetailLike';
import LessonDetailShare from './LessonDetailShare';
import LessonDetailTab from './LessonDetailTab';
const LessonDetail: FC = () => {
  const router = useRouter();
  const lessonSection = useAppSelector(selectedLessonSectionSelector);
  const canReport = useAppSelector(state => state.lessonSection.canReport);
  const isLandscape = useIsLandscape();
  const pageTitle = lessonSection?.seoTitle || lessonSection?.name || '';
  const pageCard = lessonSection ? imgProxyLoader({
    src: lessonSection?.pictureUrl,
    width: 1181
  }) : `${FILE_BROWSER_URL}/img/img-lesson-section-default.webp`;
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="LessonDetail.tsx">
        <title>{replaceHalfSpace(pageTitle)}</title>
        {lessonSection?.seoDescription && <meta name="description" content={lessonSection.seoDescription} />}
        <link rel="canonical" href={`https://ino.school/${router.asPath}`} />
        <meta property="og:locale" content="fa_IR" data-sentry-element="meta" data-sentry-source-file="LessonDetail.tsx" />
        <meta property="og:title" content={pageTitle} data-sentry-element="meta" data-sentry-source-file="LessonDetail.tsx" />
        <meta property="og:type" content="video.other" data-sentry-element="meta" data-sentry-source-file="LessonDetail.tsx" />
        <meta property="og:image" content={pageCard} data-sentry-element="meta" data-sentry-source-file="LessonDetail.tsx" />
        {lessonSection?.seoDescription && <meta property="og:description" content={lessonSection.seoDescription} />}
        {lessonSection?.updatedDate && <meta property="og:updated_time" content={lessonSection.updatedDate} />}
        <meta property="og:url" content={`${APPLICATION_LINK}${router.asPath}`} data-sentry-element="meta" data-sentry-source-file="LessonDetail.tsx" />
        <meta property="og:site_name" content={replaceHalfSpace(APPLICATION_TITLE)} data-sentry-element="meta" data-sentry-source-file="LessonDetail.tsx" />
        <meta name="twitter:card" content="summary_large_image" data-sentry-element="meta" data-sentry-source-file="LessonDetail.tsx" />
        <meta name="twitter:title" content={pageTitle} data-sentry-element="meta" data-sentry-source-file="LessonDetail.tsx" />
        {lessonSection?.seoDescription && <meta name="twitter:description" content={lessonSection.seoDescription} />}
      </Head>

      <LessonDetailBreadcrumb data-sentry-element="LessonDetailBreadcrumb" data-sentry-source-file="LessonDetail.tsx" />

      <section>
        <Container data-sentry-element="Container" data-sentry-source-file="LessonDetail.tsx">
          <div className={cs(isLandscape ? '' : 'sm:gap-14 md:flex-row', 'flex flex-col gap-8')}>
            <div className={cs(isLandscape ? '' : 'md:max-w-[calc(100%-275px)]', 'flex flex-1 flex-col ')}>
              <LessonDetailHeader data-sentry-element="LessonDetailHeader" data-sentry-source-file="LessonDetail.tsx" />
              <LessonDetailTab data-sentry-element="LessonDetailTab" data-sentry-source-file="LessonDetail.tsx" />

              <div className={cs('mb-6 flex items-center justify-between lg:mb-12', isLandscape && 'order-[-1]')}>
                <div className="flex flex-row gap-4">
                  <Box color="lightOrange" className={`relative flex h-14 items-center gap-2 border border-orangeFour px-2 before:absolute
                    before:bottom-full before:-translate-x-1/2 before:border-x-[8px] before:border-b-[10px]
                    before:border-x-transparent before:border-b-redThree ${canReport ? 'before:right-4' : 'before:left-1/2'}`} data-sentry-element="Box" data-sentry-source-file="LessonDetail.tsx">
                    {canReport ? <LessonDetailCrashReport /> : <LessonDetailCrashReportButton />}
                  </Box>
                </div>

                <Box color="lightOrange" className={`relative h-14 items-center gap-2 border border-orangeFour px-2 before:absolute
                  before:bottom-full before:left-1/2 before:-translate-x-1/2 before:border-x-[8px] before:border-b-[10px]
                  before:border-x-transparent before:border-b-redThree ${canReport ? 'hidden sm:flex' : 'flex'}`} data-sentry-element="Box" data-sentry-source-file="LessonDetail.tsx">
                  <LessonDetailBookmark data-sentry-element="LessonDetailBookmark" data-sentry-source-file="LessonDetail.tsx" />
                  {/* <LessonDetailDownload /> */}
                  <LessonDetailLike data-sentry-element="LessonDetailLike" data-sentry-source-file="LessonDetail.tsx" />
                  <LessonDetailShare data-sentry-element="LessonDetailShare" data-sentry-source-file="LessonDetail.tsx" />
                </Box>
              </div>

              <LessonDetailDescription data-sentry-element="LessonDetailDescription" data-sentry-source-file="LessonDetail.tsx" />

              {!isLandscape && <LessonDetailCommentList />}
            </div>

            <div className={cs(isLandscape ? '' : 'md:w-[275px]', '')}>
              <LessonDetailRelatedVideos data-sentry-element="LessonDetailRelatedVideos" data-sentry-source-file="LessonDetail.tsx" />
              {/* <PostList /> */}
            </div>
            {isLandscape && <LessonDetailCommentList />}
          </div>
        </Container>
      </section>
    </>;
};
export default LessonDetail;